import React, { createElement, useEffect, useState } from "react";
import Seo from "../../../Components/SeoComponent/Seo";
import {
  AlertTriangle,
  Download,
  EyeIcon,
  Loader,
  User2Icon,
} from "lucide-react";
import Header from "../../../Components/Header";
import { fetchProfile } from "../../../Services/profile/profileservice";
import {
  downloadTransunionPDF,
  getTransunionPdf,
} from "../../../Services/CreditScorePDF/CreditScorePdf";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { isAlpha, isAlphanumeric, isNumeric } from "validator";

function TransunionPdfUrl() {
  const [name, setName] = useState(null);
  const [pan, setPan] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [gender, setGender] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [transunionData, setTransunionData] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // const [errorMessage, setErrorMessage]

  const getProfileData = async () => {
    try {
      setIsLoading(true);

      const res = await fetchProfile();

      if (res?.data) {
        setProfileData(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  const isNumber = (value) => {
    if (!value) return false;

    return isNumeric(value, { no_symbols: true });
  };

  const isAlphabetic = (value) => {
    if (!value) return;

    return isAlpha(String(value).trim(), "en-IN", { ignore: " " });
  };

  const isAlphaNumericValues = (value) => {
    if (!value) return;

    return isAlphanumeric(String(value).trim(), "en-IN", { ignore: " " });
  };

  const getTransUnionPdfDetails = async () => {
    if (!profileData) return;

    const validateNumber = isNumber(mobile);
    const validateName = isAlphabetic(name);
    const validatePan = isAlphaNumericValues(pan);

    if (!validateNumber) {
      return setInputError("⚠️ Please enter number in Mobile No. field");
    } else if (mobile.length < 10) {
      return setInputError("⚠️ Enter at least 10 digits in Mobile No. field");
    } else if (!validateName) {
      return setInputError("⚠️ Enter only Characters In Full Name");
    } else if (name.length < 3) {
      return setInputError(
        "⚠️ Enter at least 3 characters in first Name field",
      );
    } else if (!validatePan) {
      return setInputError("⚠️ Enter only alphanumeric values In Pan");
    } else if (pan.length < 10) {
      return setInputError("⚠️ Enter at least 10 characters in Pan field");
    } else if (!gender) {
      return setInputError("⚠️ Please Select Gender");
    }

    setInputError(null);

    try {
      setIsLoading(true);
      const res = await getTransunionPdf({
        api_id: profileData.user_id,
        api_key: profileData.api_key,
        token_id: profileData.token_id,
        name,
        pan,
        mobile,
        gender,
        consent: "Y",
      });

      if (res?.data) {
        setTransunionData(res);
        setPdfUrl(res?.data?.report_url);

        console.log("transunion pdf response", res);
      }

      if (res?.status?.code === 200) {
        showSuccessToast(res?.message || "request successful");
      } else if (res?.status?.code === 422 || res?.status === 422) {
        showErrorToast(res?.message || res?.data?.message || "Invalid format");
      } else if (res?.status?.code === 401) {
        showErrorToast(res?.message || "Unauthorized");
      } else if (res?.status?.code === 500) {
        showErrorToast(res?.message || "server error");
      } else if (!res.success) {
        showErrorToast(res?.message || "verificaton failed");
      } else {
        showErrorToast(
          res?.status?.message || res?.data?.message || "Verification failed",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  // download PDF
  const handleDownload = async () => {
    if (!pdfUrl) {
      showErrorToast("No PDF URL available");
      return;
    }

    setIsLoading(true);
    setIsDownloading(true);

    try {
      const blob = await downloadTransunionPDF(pdfUrl);
      const url = URL.createObjectURL(blob);
      let link = document.createElement("a");
      link.href = url;
      link.download = `download transunion pdf ${Date.now()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessToast("PDF downloaded successfully");
    } catch (error) {
      console.log(error);

      showErrorToast(" Failed to download PDF");
    } finally {
      setIsLoading(false);
      setIsDownloading(false);
    }
  };

  // View PDF
  const handleViewPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    } else {
      showErrorToast("No PDF URL available");
    }
  };

  return (
    <>
      <Seo />
      <Header heading="Transunion PDF" />

      {isLoading && (
        <div className="flex justify-center mt-6">
          <Loader className="animate-spin" />
        </div>
      )}

      <div className="flex flex-col min-h-screen items-center pt-4 lg:pt-8 justify-start gap-6 bg-gray-50 px-4">
        {/* form */}

        <div className="w-full flex flex-col gap-6 justify-start items-center rounded-2xl bg-white p-6 shadow-lg">
          <h2 className="mb-6 text-center text-2xl font-semibold text-gray-800 flex items-center justify-center gap-3">
            <User2Icon />
            Enter User Details
          </h2>

          <div className=" flex items-center justify-center gap-3 flex-wrap">
            {/* Name */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="rounded-lg shadow-sm shadow-blue-600 border border-blue-600 px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* Mobile */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Mobile
              </label>
              <input
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                maxLength={10}
                type="text"
                placeholder="Enter mobile number"
                className="rounded-lg shadow-sm shadow-blue-600 border border-blue-600 px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* PAN */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                PAN
              </label>
              <input
                value={pan}
                maxLength={10}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                type="text"
                placeholder="ABCDE1234F"
                className="uppercase rounded-lg shadow-sm shadow-blue-600 border border-blue-600 px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              />
            </div>

            {/* Gender */}
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-gray-600">
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="rounded-lg shadow-sm shadow-blue-600 border border-blue-600 px-2 py-2 text-gray-800 outline-none transition focus:border-black focus:ring-1 focus:ring-black"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          {/* Submit */}
          <button
            type="button"
            onClick={getTransUnionPdfDetails}
            className="mt-6 px-6 rounded-lg bg-gradient-to-br from-purple-700 to-blue-500 py-2.5 text-white font-medium transition hover:opacity-90 active:scale-[0.98]"
          >
            Continue
          </button>
        </div>

        {/* response */}

        {transunionData && (
          <div className={`bg-green-100 p-4 text-green-800 rounded-2xl`}>
            {transunionData?.data?.message || transunionData.message}
          </div>
        )}

        {/* input error */}

        {inputError && (
          <div className="text-red-700 text-2xl font-extrabold  ">
            {inputError}
          </div>
        )}

        {/* message */}

        {transunionData?.status?.code === 200 && (
          <div className="text-green-700 text-2xl font-extrabold  ">
            {transunionData?.message ||
              transunionData?.data?.message ||
              "got transunion data successfully"}
          </div>
        )}

        {transunionData?.status?.code === 200 && (
          <div className="flex flex-col items-center gap-6 py-6 justify-center">
            <div className="text-green-600 text-2xl font-bold">
              Your Transunion Pdf request is successful. you can download pdf.
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8 xl:gap-10">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className={`flex items-center gap-2 py-2 px-4 bg-gradient-to-br rounded-lg ${isDownloading ? "from-gray-200 to-gray-50 text-black" : " from-purple-700 to-blue-600 text-white"}`}
              >
                <Download className="animate-bounce" />
                <p className="font-semibold text-2xl tracking-wider">
                  {" "}
                  {isDownloading ? "Downloading" : "Download Now"}
                </p>
              </button>

              <button
                onClick={handleViewPDF}
                className="flex items-center gap-2 py-2 px-4 bg-gradient-to-br rounded-lg from-purple-700 to-blue-600 text-white"
              >
                <EyeIcon className="animate-pulse" />
                <p className="font-semibold text-2xl tracking-wider"> View</p>
              </button>
            </div>
          </div>
        )}

        {/* instructions */}

        <div>
          <div className="bg-gradient-to-br from-purple-100 to-blue-100 text-black border p-5 mt-12 rounded-md flex flex-col items-start gap-2">
            <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
              <AlertTriangle />
              Important Instructions <span className="text-red-600">*</span>
            </h3>

            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              Please enter valid Name, mobile, Pan and Gender.
            </p>
            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              After successful verification, the system will return the PDF as
              response.
            </p>
            <p className="text-sm leading-relaxed flex items-start justify-start gap-3">
              <span>●</span>
              You may review and download the PDF for your reference or further
              use.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default TransunionPdfUrl;
